import React, { useState, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { getExternalImports, uploadExternalFile, getExternalLogs } from './hooks/externalImportService';

const LOGS_PER_PAGE = 10;
const BRAND_GREEN = '#0aa375';

const ExternalDataImport = () => {
  const [reportType, setReportType] = useState('0');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsModal, setLogsModal] = useState({
    open: false, logs: [], totalCount: 0, totalPages: 0, row: null
  });
  const hasFetched = useRef(false);

  const fetchData = async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    try {
      const res = await getExternalImports(reportType);
      setData(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error('Failed to load import history');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    hasFetched.current = false;
    fetchData();
  }, [reportType]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'numeric', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true
    });
  };

      const Spinner = () => (
        <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

  const openAddPopup = () => {
    Swal.fire({
      title: 'Add External File',
      html: `
        <div style="font-family: Calibri; font-size: 14px; text-align: left; padding: 10px 0;">
          <div class="mb-3">
            <label class="fw-semibold mb-2 d-block">Report Type</label>
            <div class="d-flex gap-4">
              <label><input type="radio" name="reportType" value="1" checked> Payment</label>
              <label><input type="radio" name="reportType" value="2"> Denial</label>
            </div>
          </div>
          <div class="mb-3">
            <label class="fw-semibold d-block mb-1">Select Excel File</label>
            <input type="file" id="popupFileInput" accept=".xlsx,.xls" class="form-control form-control-sm" />
          </div>
        </div>
      `,
      width: '340px', padding: '15px',
      showCancelButton: true,
      confirmButtonText: 'Upload', cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-success btn-sm px-4 py-1.5',
        cancelButton: 'btn btn-secondary btn-sm px-4 py-1.5',
        title: 'py-2 fs-5',
        popup: 'border-0 shadow-lg rounded',
        htmlContainer: 'p-0 m-0',
        actions: 'pt-2 pb-1 gap-2'
      },
      didOpen: () => {
        const confirmBtn = Swal.getConfirmButton();
        if (confirmBtn) confirmBtn.style.backgroundColor = '#0aa375';
      },
      preConfirm: () => {
        const selectedType = document.querySelector('input[name="reportType"]:checked')?.value;
        const file = document.getElementById('popupFileInput').files[0];
        if (!file) { Swal.showValidationMessage('Please select a file'); return false; }
        if (!selectedType) { Swal.showValidationMessage('Please select report type'); return false; }
        return { file, reportType: parseInt(selectedType) };
      }
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        const { file, reportType } = result.value;
        const userId = sessionStorage.getItem('userId');
        setLoading(true);
        try {
          const res = await uploadExternalFile(file, reportType, parseInt(userId));
          if (res.success === true || res.message?.toLowerCase().includes('success')) {
            toast.success('File Uploaded Successfully!', { duration: 4000 });
            setReportType(reportType.toString());
            hasFetched.current = false;
            fetchData();
          } else {
            toast.error(res.message || 'Upload Failed', { duration: 6000 });
          }
        } catch (err) {
          const errorMsg = err.response?.data?.message || err.message || 'Upload failed';
          toast.error(`❌ ${errorMsg}`, { duration: 6000 });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleViewLogs = async (row, page = 1) => {
    try {
      const reportTypeNum = row.ReportType || (row.ReportTypeName === 'Payment' ? 1 : 2);
      const res = await getExternalLogs(row.Id, reportTypeNum, page, LOGS_PER_PAGE);

      if (res?.data?.length > 0) {
        setLogsPage(page);
        setLogsModal({
          open: true,
          logs: res.data,
          totalCount: res.totalCount,
          totalPages: res.totalPages,
          row
        });
      } else {
        Swal.fire({ title: '', text: 'File is not processing yet.', icon: 'warning' });
      }
    } catch (err) {
      toast.error('Failed to load logs');
    }
  };

  const closeLogsModal = () => {
    setLogsModal({ open: false, logs: [], totalCount: 0, totalPages: 0, row: null });
    setLogsPage(1);
  };

  const columns = [
    { name: 'Transaction Type', selector: row => row.ReportTypeName || row.ReportType, sortable: true, width: '150px' },
    { name: 'FileName', selector: row => row.AzureFileName, sortable: true, wrap: true, minWidth: '170px' },
    { name: 'Created By', selector: row => row.CreatedBy, width: '110px' },
    { name: 'Created Date', selector: row => formatDate(row.CreatedDate), sortable: true, width: '170px' },
    { name: 'Status', selector: row => row.Status, width: '95px' },
    { name: 'Date Processed', selector: row => formatDate(row.DateProceed), width: '175px' },
    {
      name: 'Log',
      cell: (row) => (
        <button
          className="btn btn-sm text-white px-1 py-1 text-xs"
          style={{ fontSize: '10px', backgroundColor: '#0aa375', border: 'none' }}
          onClick={() => handleViewLogs(row)}
        >View Log</button>
      ),
      center: true, width: '85px'
    }
  ];

  return (
    <div className="container-fluid pt-1 pb-2" style={{ height: 'calc(100vh - 56px)', padding: 0 }}>
      <div className="card shadow border-0 h-100 d-flex flex-column">
        <div className="p-2 border-bottom bg-white">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label small mb-0 fw-semibold">Transaction Type:</label>
              <select
                className="form-select form-select-sm"
                style={{ width: '155px' }}
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                <option value="0">All</option>
                <option value="1">Payment</option>
                <option value="2">Denial</option>
              </select>
            </div>
            <button
              className="btn btn-sm text-white px-4 py-1.5 fw-semibold"
              style={{ backgroundColor: '#0aa375', border: 'none' }}
              onClick={openAddPopup}
              disabled={loading}
            >
              <i className="fa fa-plus me-1"></i> Add External File
            </button>
          </div>
        </div>

        <div className="flex-grow-1 p-2 overflow-auto">
          <DataTable
            columns={columns} data={data} pagination
            paginationPerPage={15} progressPending={loading}
            progressComponent={<Spinner />}
            highlightOnHover pointerOnHover responsive dense
            noDataComponent="No import records found"
          />
        </div>
      </div>

      {logsModal.open && (
        <div
          onClick={closeLogsModal}
          style={{
            position: 'fixed', inset: 0, zIndex: 1050,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'fadeIn 0.15s ease',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 700, maxWidth: '95vw', maxHeight: '85vh',
              background: '#fff', borderRadius: 14,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden', animation: 'slideUp 0.2s ease',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #0aa375 0%, #07875f 100%)',
              padding: '14px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 7,
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Log Details</div>
                  {/* ✅ Shows total count from API */}
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>
                    {logsModal.totalCount} entr{logsModal.totalCount === 1 ? 'y' : 'ies'}
                  </div>
                </div>
              </div>
              <button
                onClick={closeLogsModal}
                style={{
                  width: 28, height: 28, borderRadius: 6, border: 'none',
                  background: 'rgba(255,255,255,0.2)', color: '#fff',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, lineHeight: 1,
                }}
              >×</button>
            </div>

            {/* Table */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['#', 'Message', 'Date'].map((h, i) => (
                      <th key={h} style={{
                        position: 'sticky', top: 0, zIndex: 2,
                        padding: '9px 14px', background: '#f0fdf8',
                        borderBottom: '2px solid #6ee7b7',
                        fontWeight: 700, color: '#065f46', fontSize: 12,
                        textAlign: i === 0 ? 'center' : 'left',
                        width: i === 0 ? 40 : i === 2 ? 150 : 'auto',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logsModal.logs.map((log, i) => (
                    <tr key={i}
                      style={{ background: i % 2 === 0 ? '#fff' : '#f9fefe' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#ecfdf5'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f9fefe'}
                    >
                      {/* ✅ Correct row number across pages */}
                      <td style={{
                        padding: '8px 14px', textAlign: 'center',
                        color: '#9ca3af', fontSize: 11, fontWeight: 600,
                        borderBottom: '1px solid #f3f4f6',
                      }}>{(logsPage - 1) * LOGS_PER_PAGE + i + 1}</td>
                      <td style={{
                        padding: '8px 14px', color: '#1f2937', lineHeight: 1.5,
                        borderBottom: '1px solid #f3f4f6',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {log.message || log.Message || '—'}
                      </td>
                      <td style={{
                        padding: '8px 14px', whiteSpace: 'nowrap',
                        color: '#6b7280', fontSize: 12,
                        borderBottom: '1px solid #f3f4f6',
                      }}>
                        {formatDate(log.logDate || log.LogDate || log.LogDateFrmt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ✅ Footer with pagination */}
            <div style={{
              padding: '10px 20px', borderTop: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#fafafa',
            }}>
              {/* Showing X-Y of N */}
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Showing {(logsPage - 1) * LOGS_PER_PAGE + 1}–{Math.min(logsPage * LOGS_PER_PAGE, logsModal.totalCount)} of {logsModal.totalCount}
              </span>

              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  onClick={() => handleViewLogs(logsModal.row, logsPage - 1)}
                  disabled={logsPage === 1}
                  style={{
                    padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                    background: logsPage === 1 ? '#f3f4f6' : '#fff',
                    color: logsPage === 1 ? '#9ca3af' : '#0aa375',
                    cursor: logsPage === 1 ? 'not-allowed' : 'pointer', fontSize: 12
                  }}
                >‹ Prev</button>

                {Array.from({ length: logsModal.totalPages }, (_, i) => i + 1)
                  .filter(p => {
                    if (logsModal.totalPages <= 7) return true;
                    if (p === 1 || p === logsModal.totalPages) return true;
                    if (p >= logsPage - 2 && p <= logsPage + 2) return true;
                    return false;
                  })
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', fontSize: 12, padding: '0 2px' }}>...</span>
                    ) : (
                      <button key={p}
                        onClick={() => handleViewLogs(logsModal.row, p)}
                        style={{
                          width: 28, height: 28, borderRadius: 6, border: '1px solid #d1fae5',
                          background: logsPage === p ? '#0aa375' : '#fff',
                          color: logsPage === p ? '#fff' : '#0aa375',
                          fontWeight: logsPage === p ? 700 : 400,
                          cursor: 'pointer', fontSize: 12
                        }}
                      >{p}</button>
                    )
                  )
                }

                <button
                  onClick={() => handleViewLogs(logsModal.row, logsPage + 1)}
                  disabled={logsPage === logsModal.totalPages}
                  style={{
                    padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                    background: logsPage === logsModal.totalPages ? '#f3f4f6' : '#fff',
                    color: logsPage === logsModal.totalPages ? '#9ca3af' : '#0aa375',
                    cursor: logsPage === logsModal.totalPages ? 'not-allowed' : 'pointer', fontSize: 12
                  }}
                >Next ›</button>
              </div>

              <button
                onClick={closeLogsModal}
                style={{
                  padding: '7px 24px', borderRadius: 7, border: 'none',
                  background: '#0aa375', color: '#fff',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}
              >Close</button>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
            @keyframes slideUp { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: translateY(0) } }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default ExternalDataImport;